/**
 * TariffProfileChart - Bar chart of average hourly electricity tariffs over 24 hours.
 *
 * Displays:
 *   - Import tariff (purchase from grid)
 *   - Export tariff (feed-in to grid)
 *
 * Data sources (in priority order):
 *   1. `data` prop with real hourly tariff data (from uploaded dataset)
 *   2. `communityTariffs` prop with flat rates from community metadata
 *      (generates 24h profile with peak-hour multiplier: 1.2x at 7-9, 17-21)
 *   3. Empty state when no tariff data is available
 *
 * Props:
 *   - data: Optional external tariff data override { hours, importTariff, exportTariff }
 *   - communityTariffs: Optional { importTariff: number, exportTariff: number } flat rates
 *   - currency: Currency symbol (default: 'EUR')
 */

import React, { useMemo } from 'react'
import Chart from 'react-apexcharts'

function generateProfileFromFlat(importRate, exportRate) {
  const hours = Array.from({ length: 24 }, (_, i) => i)
  const importTariff = hours.map(h => {
    if ((h >= 7 && h <= 9) || (h >= 17 && h <= 21)) {
      return parseFloat((importRate * 1.2).toFixed(4))
    }
    return importRate
  })
  const exportTariff = hours.map(() => exportRate)
  return { hours, importTariff, exportTariff }
}

function TariffProfileChart({ data, communityTariffs, currency = 'EUR' }) {
  const chartData = useMemo(() => {
    if (data && data.importTariff && data.importTariff.length > 0) return data
    if (communityTariffs && communityTariffs.importTariff > 0) {
      return generateProfileFromFlat(
        communityTariffs.importTariff,
        communityTariffs.exportTariff || 0
      )
    }
    return null
  }, [data, communityTariffs])

  if (!chartData) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ height: 320, color: 'var(--color-neutral-500)', fontSize: 'var(--font-size-sm)' }}
      >
        No tariff data available. Configure tariffs in community settings.
      </div>
    )
  }

  const yMax = Math.max(
    ...chartData.importTariff,
    ...chartData.exportTariff,
    0.10
  )

  const chartOptions = {
    chart: {
      type: 'bar',
      height: 350,
      toolbar: { show: false },
      animations: { enabled: true, speed: 800 },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '85%',
        borderRadius: 2,
        dataLabels: { position: 'top' },
      },
    },
    colors: ['#34495E', '#27AE60'],
    dataLabels: { enabled: false },
    stroke: { show: true, width: 1, colors: ['transparent'] },
    legend: {
      position: 'top',
      horizontalAlign: 'center',
      fontSize: '12px',
      fontFamily: 'var(--font-family-base)',
      markers: { width: 10, height: 10, radius: 2 },
      itemMargin: { horizontal: 10, vertical: 4 },
    },
    xaxis: {
      categories: chartData.hours,
      title: {
        text: 'Hour',
        style: { fontSize: '11px', fontWeight: 500 },
      },
      labels: { style: { fontSize: '10px' }, rotate: 0 },
      axisBorder: { show: true, color: '#e0e0e0' },
      axisTicks: { show: true, color: '#e0e0e0' },
    },
    yaxis: {
      title: {
        text: `Price (${currency}/kWh)`,
        style: { fontSize: '11px', fontWeight: 500 },
      },
      labels: {
        style: { fontSize: '10px' },
        formatter: (value) => value != null ? value.toFixed(2) : '',
      },
      min: 0,
      max: parseFloat((yMax * 1.15).toFixed(2)),
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (value) => value != null ? `${value.toFixed(3)} ${currency}/kWh` : '',
      },
    },
    grid: {
      borderColor: '#e0e0e0',
      strokeDashArray: 3,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
  }

  const series = [
    { name: 'Import Tariff', data: chartData.importTariff },
    { name: 'Export Tariff', data: chartData.exportTariff },
  ]

  return (
    <div>
      <div
        className="mb-3"
        style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-neutral-600)',
          textAlign: 'center',
        }}
      >
        {chartData.source === 'day_ahead' ? 'Day-ahead tariff profile (tomorrow)'
          : chartData.source === 'historical' ? 'Average hourly tariff profile (from historical data)'
          : chartData.source === 'metadata' ? 'Estimated tariff profile (from community settings)'
          : 'Hourly tariff profile'}
      </div>
      <Chart options={chartOptions} series={series} type="bar" height={320} />
    </div>
  )
}

export default TariffProfileChart
